import { useCallback, useEffect, useRef, useState } from 'react';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import {
  loginWithCode,
  sendLoginCode,
  validateMembership,
  type AuthApiOptions,
  type AuthApiError,
} from '../adapters/auth/authApi.ts';
import { getUiLocale, t } from '../i18n/index.ts';
import type { StringKey } from '../i18n/strings.ts';
import { useMembership, type MembershipState } from './useMembership.ts';

export type AuthBusyKind = 'send' | 'submit';

export type UseAuthResult = MembershipState & {
  busy: boolean;
  /** Which auth action is in flight — drives per-button busy labels. */
  busyKind: AuthBusyKind | null;
  message: string | null;
  messageKind: 'ok' | 'error' | null;
  sendCode: (email: string) => Promise<boolean>;
  /** `active` closes the dialog; other outcomes keep it open so the status is visible. */
  submitCode: (code: string) => Promise<'active' | 'inactive' | 'session_expired' | false>;
  logout: () => Promise<void>;
  revalidate: () => Promise<boolean>;
  clearMessage: () => void;
};

export type UseAuthOptions = AuthApiOptions & {
  /** Re-validate stored credentials on mount (default true). */
  revalidateOnMount?: boolean;
  appName?: string;
  languageCode?: string;
};

/**
 * Email-code auth + validate. Tokens are persisted via SettingsStore only —
 * never written to console.
 */
export function useAuth(
  settingsStore: SettingsStore,
  options: UseAuthOptions = {},
): UseAuthResult {
  const membership = useMembership(settingsStore);
  const [busyKind, setBusyKind] = useState<AuthBusyKind | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [messageKind, setMessageKind] = useState<'ok' | 'error' | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const clearMessage = useCallback(() => {
    setMessage(null);
    setMessageKind(null);
  }, []);

  const revalidate = useCallback(async () => {
    const snap = settingsStore.getSnapshot?.() ?? (await settingsStore.getAll());
    if (!snap.userId || !snap.userToken) return false;
    const result = await validateMembership(snap.userId, snap.userToken, options);
    await settingsStore.set({ statusJson: result.statusJson });
    return result.unlock;
  }, [settingsStore, options]);

  useEffect(() => {
    if (options.revalidateOnMount === false) return;
    void revalidate();
    // intentionally once on mount / when credentials appear
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membership.userId, membership.userToken]);

  useEffect(() => {
    const applyCreds = async (userId: string, userToken: string) => {
      if (!userId || !userToken) return;
      await settingsStore.set({ userId, userToken });
      const validated = await validateMembership(userId, userToken, options);
      await settingsStore.set({ statusJson: validated.statusJson });
      try {
        localStorage.removeItem('rikosub:patreon-handoff');
      } catch {
        // ignore
      }
      if (mounted.current) {
        setMessage(t(validated.unlock ? 'loginMsgSignedIn' : 'loginMsgInactive'));
        setMessageKind(validated.unlock ? 'ok' : 'error');
      }
    };

    const onMessage = (event: MessageEvent) => {
      // Same-origin LoginCallbackPage, or www Patreon callback (shared extension redirect).
      const allowed =
        event.origin === window.location.origin
        || event.origin === 'https://www.netflixsubs.app'
        || event.origin === 'https://netflixsubs.app';
      if (!allowed) return;
      const data = event.data as { type?: string; userId?: string; userToken?: string } | null;
      if (!data || data.type !== 'rikosub:patreon-login') return;
      void applyCreds(data.userId ?? '', data.userToken ?? '');
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key !== 'rikosub:patreon-handoff' || !event.newValue) return;
      try {
        const parsed = JSON.parse(event.newValue) as { userId?: string; userToken?: string };
        void applyCreds(parsed.userId ?? '', parsed.userToken ?? '');
      } catch {
        // ignore
      }
    };

    window.addEventListener('message', onMessage);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
    };
  }, [settingsStore, options]);

  const sendCode = useCallback(
    async (email: string) => {
      setBusyKind('send');
      clearMessage();
      try {
        const result = await sendLoginCode(email, {
          ...options,
          languageCode: options.languageCode ?? getUiLocale(),
          // Match extension youtube flavor appName via browser i18n getMessage.
          appName: options.appName ?? t('appName'),
        });
        if (!mounted.current) return false;
        if (result.ok) {
          setMessage(t('loginMsgEmailSent'));
          setMessageKind('ok');
          return true;
        }
        setMessage(mapSendError(result.error));
        setMessageKind('error');
        return false;
      } finally {
        if (mounted.current) setBusyKind(null);
      }
    },
    [options, clearMessage],
  );

  const submitCode = useCallback(
    async (code: string): Promise<'active' | 'inactive' | 'session_expired' | false> => {
      setBusyKind('submit');
      clearMessage();
      try {
        const result = await loginWithCode(code.replace(/\s+/g, '').trim(), options);
        if (!mounted.current) return false;
        if (!result.ok) {
          setMessage(
            result.error.code === 'invalid_code'
              ? t('loginMsgInvalidCode')
              : result.error.message || t('loginMsgNetworkError'),
          );
          setMessageKind('error');
          return false;
        }
        // Persist credentials — do not log tokens.
        await settingsStore.set({
          userId: result.userId,
          userToken: result.userToken,
        });
        const validated = await validateMembership(result.userId, result.userToken, options);
        await settingsStore.set({ statusJson: validated.statusJson });
        if (!mounted.current) return false;
        if (validated.unlock) {
          setMessage(t('loginMsgSignedIn'));
          setMessageKind('ok');
          return 'active';
        }
        if (validated.status.response === 'Session Expired') {
          setMessage(t('loginMsgSessionExpired'));
          setMessageKind('error');
          return 'session_expired';
        }
        setMessage(t('loginMsgInactive'));
        setMessageKind('error');
        return 'inactive';
      } finally {
        if (mounted.current) setBusyKind(null);
      }
    },
    [options, settingsStore, clearMessage],
  );

  const logout = useCallback(async () => {
    await settingsStore.set({
      userId: '',
      userToken: '',
      statusJson: '',
    });
    clearMessage();
  }, [settingsStore, clearMessage]);

  return {
    ...membership,
    busy: busyKind != null,
    busyKind,
    message,
    messageKind,
    sendCode,
    submitCode,
    logout,
    revalidate,
    clearMessage,
  };
}

function mapSendError(error: AuthApiError): string {
  const byCode: Partial<Record<string, StringKey>> = {
    not_found: 'loginMsgNotSubscriber',
    quota_exceeded: 'loginMsgQuotaExceeded',
    rate_limited: 'loginMsgRateLimited',
    invalid_request: 'loginMsgEnterEmail',
  };
  const key = byCode[error.code];
  if (key) return t(key);
  return error.message || t('loginMsgNetworkError');
}
